import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import Map, { AttributionControl, Layer, NavigationControl, ScaleControl, Source } from 'react-map-gl/maplibre'
import type { MapRef, MapLayerMouseEvent, ViewStateChangeEvent } from 'react-map-gl/maplibre'
import 'maplibre-gl/dist/maplibre-gl.css'
import type { AirportPoint, LiveAircraft } from '@/types/airTraffic'
import { AIR, CLASS_COLOR, FONT_MONO, classify, fmtAlt, type AircraftClass } from './theme'
import { projectPosition, type AirLive, type BBox } from './useAirLive'

const OFM = 'https://tiles.openfreemap.org/planet'
const GLYPHS = 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf'
const FONT = ['Noto Sans Regular']
const ATTRIBUTION =
  'Aircraft data © <a href="https://adsb.lol" target="_blank" rel="noopener">adsb.lol</a> contributors (ODbL) · © The OpenSky Network'

const overlayLayers: any[] = [
  {
    id: 'boundary-state', type: 'line', source: 'omt', 'source-layer': 'boundary', minzoom: 4,
    filter: ['==', ['get', 'admin_level'], 4],
    paint: { 'line-color': '#1E2630', 'line-width': 0.6, 'line-dasharray': [2, 2] },
  },
  {
    id: 'boundary-country', type: 'line', source: 'omt', 'source-layer': 'boundary',
    filter: ['all', ['==', ['get', 'admin_level'], 2], ['!=', ['get', 'maritime'], 1]],
    paint: { 'line-color': '#3A4656', 'line-width': ['interpolate', ['linear'], ['zoom'], 2, 0.6, 8, 1.4] },
  },
  {
    id: 'place-country', type: 'symbol', source: 'omt', 'source-layer': 'place', maxzoom: 7,
    filter: ['==', ['get', 'class'], 'country'],
    layout: {
      'text-field': ['coalesce', ['get', 'name:en'], ['get', 'name']], 'text-font': FONT,
      'text-size': ['interpolate', ['linear'], ['zoom'], 2, 9, 6, 12], 'text-transform': 'uppercase',
      'text-letter-spacing': 0.18,
    },
    paint: { 'text-color': '#5A6472', 'text-halo-color': AIR.bg, 'text-halo-width': 1.2 },
  },
  {
    id: 'place-city', type: 'symbol', source: 'omt', 'source-layer': 'place', minzoom: 5,
    filter: ['==', ['get', 'class'], 'city'],
    layout: { 'text-field': ['coalesce', ['get', 'name:en'], ['get', 'name']], 'text-font': FONT, 'text-size': 11 },
    paint: { 'text-color': '#7A8594', 'text-halo-color': AIR.bg, 'text-halo-width': 1.2 },
  },
]

const DARK_STYLE: any = {
  version: 8,
  glyphs: GLYPHS,
  sources: { omt: { type: 'vector', url: OFM } },
  layers: [
    { id: 'bg', type: 'background', paint: { 'background-color': AIR.land } },
    { id: 'water', type: 'fill', source: 'omt', 'source-layer': 'water', paint: { 'fill-color': AIR.water } },
    {
      id: 'landuse-military', type: 'fill', source: 'omt', 'source-layer': 'landuse', minzoom: 7,
      filter: ['==', ['get', 'class'], 'military'], paint: { 'fill-color': '#3A2A12', 'fill-opacity': 0.35 },
    },
    {
      id: 'aeroway-area', type: 'fill', source: 'omt', 'source-layer': 'aeroway', minzoom: 10,
      filter: ['match', ['geometry-type'], ['Polygon', 'MultiPolygon'], true, false],
      paint: { 'fill-color': '#161D26' },
    },
    {
      id: 'roads', type: 'line', source: 'omt', 'source-layer': 'transportation', minzoom: 6,
      filter: ['match', ['get', 'class'], ['motorway', 'trunk', 'primary'], true, false],
      paint: { 'line-color': '#18202A', 'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.4, 12, 2] },
    },
    {
      id: 'runway', type: 'line', source: 'omt', 'source-layer': 'aeroway', minzoom: 9,
      filter: ['match', ['get', 'class'], ['runway'], true, false],
      paint: { 'line-color': '#334051', 'line-width': ['interpolate', ['linear'], ['zoom'], 9, 1, 14, 14] },
    },
    {
      id: 'taxiway', type: 'line', source: 'omt', 'source-layer': 'aeroway', minzoom: 12,
      filter: ['match', ['get', 'class'], ['taxiway'], true, false],
      paint: { 'line-color': '#253040', 'line-width': ['interpolate', ['linear'], ['zoom'], 12, 0.5, 15, 3] },
    },
    ...overlayLayers,
  ],
}

const SAT_STYLE: any = {
  version: 8,
  glyphs: GLYPHS,
  sources: {
    esri: {
      type: 'raster', tileSize: 256, attribution: 'Imagery © Esri',
      tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
    },
    omt: { type: 'vector', url: OFM },
  },
  layers: [
    { id: 'esri', type: 'raster', source: 'esri', paint: { 'raster-brightness-max': 0.75, 'raster-saturation': -0.35 } },
    ...overlayLayers,
  ],
}

const CLASSES: AircraftClass[] = ['civil', 'military', 'privacy', 'watched', 'emergency']

/** Plane silhouette per class, drawn once on a canvas (crisper than SDF tinting). */
function planeImage(fill: string, ground = false): ImageData {
  const size = 64
  const c = document.createElement('canvas')
  c.width = c.height = size
  const ctx = c.getContext('2d')!
  ctx.beginPath()
  const p: [number, number][] = [
    [32, 3], [35.5, 10], [35.5, 24], [59, 37], [59, 42], [35.5, 35], [35.5, 50], [44, 56], [44, 60], [32, 57],
    [20, 60], [20, 56], [28.5, 50], [28.5, 35], [5, 42], [5, 37], [28.5, 24], [28.5, 10],
  ]
  ctx.moveTo(p[0][0], p[0][1])
  for (const [x, y] of p.slice(1)) ctx.lineTo(x, y)
  ctx.closePath()
  ctx.lineJoin = 'round'
  ctx.lineWidth = 4
  ctx.strokeStyle = AIR.bg
  ctx.stroke()
  ctx.globalAlpha = ground ? 0.55 : 1
  ctx.fillStyle = fill
  ctx.fill()
  return ctx.getImageData(0, 0, size, size)
}

function addPlaneImages(map: any) {
  for (const cls of CLASSES) {
    for (const g of [false, true]) {
      const id = `plane-${cls}${g ? '-g' : ''}`
      if (!map.hasImage(id)) map.addImage(id, planeImage(CLASS_COLOR[cls], g), { pixelRatio: 2 })
    }
  }
}

export interface AirFilters {
  milOnly: boolean
  altBand: 'all' | 'low' | 'mid' | 'high' | 'ground'
  text: string
}

export interface RouteLine {
  origin: [number, number] | null
  destination: [number, number] | null
  ended?: boolean
}

export interface SelectionOverlay {
  hex?: string
  fr24Path?: [number, number][]
  history?: [number, number][]
  /** hex -> FR24 route; drawn as great circles origin -> aircraft -> destination */
  routes?: Record<string, RouteLine>
  ring?: { lon: number; lat: number; km: number } | null
}

export interface AirMapHandle {
  flyTo: (lon: number, lat: number, zoom?: number) => void
  getBBox: () => BBox | null
  getCenter: () => { lon: number; lat: number; zoom: number } | null
}

interface Props {
  live: AirLive
  basemap: 'dark' | 'satellite'
  filters: AirFilters
  showAirports: boolean
  airports: AirportPoint[]
  selection: SelectionOverlay
  onViewChange: (bbox: BBox, zoom: number) => void
  onSelectAircraft: (hex: string) => void
  onSelectAirport: (icao: string) => void
}

function passesFilters(a: LiveAircraft, f: AirFilters, cls: AircraftClass): boolean {
  if (f.milOnly && !a.mil) return false
  if (f.altBand !== 'all' && cls !== 'emergency') {
    const alt = a.alt ?? 0
    if (f.altBand === 'ground' && !a.gnd) return false
    if (f.altBand === 'low' && (a.gnd || alt >= 10000)) return false
    if (f.altBand === 'mid' && (a.gnd || alt < 10000 || alt >= 25000)) return false
    if (f.altBand === 'high' && (a.gnd || alt < 25000)) return false
  }
  if (f.text) {
    const q = f.text.toLowerCase()
    const hay = `${a.cs || ''} ${a.reg || ''} ${a.type || ''} ${a.op || ''} ${a.hex}`.toLowerCase()
    if (!hay.includes(q)) return false
  }
  return true
}

const EMPTY: any = { type: 'FeatureCollection', features: [] }

/** Great-circle arc between two points, longitudes kept continuous across the antimeridian. */
function greatCircle(a: [number, number], b: [number, number], n = 48): [number, number][] {
  const r = Math.PI / 180
  const [l1, p1, l2, p2] = [a[0] * r, a[1] * r, b[0] * r, b[1] * r]
  const d = 2 * Math.asin(Math.sqrt(Math.sin((p2 - p1) / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin((l2 - l1) / 2) ** 2))
  if (d < 1e-6) return [a, b]
  const out: [number, number][] = []
  for (let i = 0; i <= n; i++) {
    const f = i / n
    const A = Math.sin((1 - f) * d) / Math.sin(d)
    const B = Math.sin(f * d) / Math.sin(d)
    const x = A * Math.cos(p1) * Math.cos(l1) + B * Math.cos(p2) * Math.cos(l2)
    const y = A * Math.cos(p1) * Math.sin(l1) + B * Math.cos(p2) * Math.sin(l2)
    const z = A * Math.sin(p1) + B * Math.sin(p2)
    let lon = Math.atan2(y, x) / r
    const lat = Math.atan2(z, Math.sqrt(x * x + y * y)) / r
    if (out.length) {
      const prev = out[out.length - 1][0]
      while (lon - prev > 180) lon -= 360
      while (lon - prev < -180) lon += 360
    }
    out.push([lon, lat])
  }
  return out
}

function circlePolygon(lon: number, lat: number, km: number): [number, number][] {
  const pts: [number, number][] = []
  for (let i = 0; i <= 64; i++) {
    const b = (i / 64) * 2 * Math.PI
    const dLat = (km / 111) * Math.cos(b)
    const dLon = (km / (111 * Math.cos((lat * Math.PI) / 180))) * Math.sin(b)
    pts.push([lon + dLon, lat + dLat])
  }
  return pts
}

const AirMap = forwardRef<AirMapHandle, Props>(function AirMap(
  { live, basemap, filters, showAirports, airports, selection, onViewChange, onSelectAircraft, onSelectAirport },
  ref,
) {
  const mapRef = useRef<MapRef>(null)
  const [hover, setHover] = useState<{ x: number; y: number; a: LiveAircraft } | null>(null)
  const filtersRef = useRef(filters)
  filtersRef.current = filters
  const watchedRef = useRef(live.watched)
  watchedRef.current = live.watched

  useImperativeHandle(ref, () => ({
    flyTo: (lon, lat, zoom) => mapRef.current?.flyTo({ center: [lon, lat], zoom: zoom ?? Math.max(mapRef.current.getZoom(), 7), duration: 1200 }),
    getBBox: () => {
      const b = mapRef.current?.getBounds()
      return b ? [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()] : null
    },
    getCenter: () => {
      const m = mapRef.current
      if (!m) return null
      const c = m.getCenter()
      return { lon: c.lng, lat: c.lat, zoom: m.getZoom() }
    },
  }))

  const emitView = useCallback(() => {
    const m = mapRef.current
    if (!m) return
    const b = m.getBounds()
    const clampLon = (v: number) => Math.max(-180, Math.min(180, v))
    let west = b.getWest()
    let east = b.getEast()
    if (east - west >= 360) {
      west = -180
      east = 180
    } else {
      west = ((((west + 180) % 360) + 360) % 360) - 180
      east = ((((east + 180) % 360) + 360) % 360) - 180
    }
    onViewChange([clampLon(west), Math.max(-85, b.getSouth()), clampLon(east), Math.min(85, b.getNorth())], m.getZoom())
  }, [onViewChange])

  // Aircraft + trails: rebuilt twice a second from the live refs, positions dead-reckoned.
  useEffect(() => {
    const tick = () => {
      const map = mapRef.current?.getMap()
      if (!map || !map.isStyleLoaded()) return
      const acSrc = map.getSource('ac') as any
      const trSrc = map.getSource('trails') as any
      if (!acSrc || !trSrc) return
      const now = Date.now() / 1000
      const f = filtersRef.current
      const watched = watchedRef.current
      const feats: any[] = []
      const lines: any[] = []
      for (const a of live.aircraft.current.values()) {
        const cls = classify(a, watched)
        if (!passesFilters(a, f, cls)) continue
        const pos = projectPosition(a, now)
        feats.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: pos },
          properties: {
            hex: a.hex, c: cls, g: a.gnd ? 1 : 0, trk: a.trk ?? 0,
            lbl: a.cs || a.reg || a.hex.toUpperCase(), n: cls === 'civil' ? 0 : 1,
            icon: `plane-${cls}${a.gnd ? '-g' : ''}`,
          },
        })
        const t = live.trails.current.get(a.hex)
        if (t && t.length > 1 && !a.gnd) {
          lines.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: [...t, pos] }, properties: { c: cls } })
        }
      }
      acSrc.setData({ type: 'FeatureCollection', features: feats })
      trSrc.setData({ type: 'FeatureCollection', features: lines })
    }
    tick()
    const id = window.setInterval(tick, 500)
    return () => window.clearInterval(id)
  }, [live.aircraft, live.trails, live.version, basemap])

  const airportData = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: (showAirports ? airports : []).map((a) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [a.lon, a.lat] },
        properties: { icao: a.icao, lbl: a.iata || a.icao, mil: a.mil ? 1 : 0, atc: a.atc ? 1 : 0 },
      })),
    }),
    [airports, showAirports],
  )

  const selData = useMemo(() => {
    const features: any[] = []
    if (selection.history && selection.history.length > 1)
      features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: selection.history }, properties: { k: 'history' } })
    if (selection.fr24Path && selection.fr24Path.length > 1)
      features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: selection.fr24Path }, properties: { k: 'flown' } })
    const endpoints = new Set<string>()
    for (const [hx, r] of Object.entries(selection.routes || {})) {
      const ac = live.aircraft.current.get(hx)
      if (!ac) continue
      const sel = hx === selection.hex ? 1 : 0
      const pos: [number, number] = [ac.lon, ac.lat]
      if (r.origin && !(sel && selection.fr24Path))
        features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: greatCircle(r.origin, pos) }, properties: { k: 'route-flown', sel } })
      if (r.destination && !r.ended)
        features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: greatCircle(pos, r.destination) }, properties: { k: 'route-next', sel } })
      for (const p of [r.origin, r.destination]) {
        if (!p || endpoints.has(p.join(','))) continue
        endpoints.add(p.join(','))
        features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: p }, properties: { k: 'endpoint' } })
      }
    }
    if (selection.ring)
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: circlePolygon(selection.ring.lon, selection.ring.lat, selection.ring.km) },
        properties: { k: 'ring' },
      })
    return { type: 'FeatureCollection', features }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, live.version])

  const onMouseMove = useCallback(
    (e: MapLayerMouseEvent) => {
      const f = e.features?.find((x) => x.layer.id === 'ac-icon')
      if (!f) return setHover(null)
      const a = live.aircraft.current.get(f.properties?.hex)
      setHover(a ? { x: e.point.x, y: e.point.y, a } : null)
    },
    [live.aircraft],
  )

  const onClick = useCallback(
    (e: MapLayerMouseEvent) => {
      const f = e.features?.[0]
      if (!f) return
      if (f.layer.id === 'ac-icon') onSelectAircraft(f.properties?.hex)
      else if (f.layer.id === 'apt-dot') onSelectAirport(f.properties?.icao)
    },
    [onSelectAircraft, onSelectAirport],
  )

  const colorExpr: any = ['match', ['get', 'c'], ...CLASSES.flatMap((c) => [c, CLASS_COLOR[c]]), AIR.civil]

  return (
    <div className="relative h-full w-full" style={{ background: AIR.bg }}>
      <Map
        ref={mapRef}
        initialViewState={{ longitude: 50, latitude: 27, zoom: 4.2 }}
        mapStyle={basemap === 'dark' ? DARK_STYLE : SAT_STYLE}
        style={{ width: '100%', height: '100%' }}
        attributionControl={false}
        dragRotate={false}
        maxPitch={0}
        renderWorldCopies={false}
        interactiveLayerIds={['ac-icon', 'apt-dot']}
        cursor={hover ? 'pointer' : 'grab'}
        onLoad={(e) => {
          addPlaneImages(e.target)
          e.target.on('styleimagemissing', (ev: any) => {
            if (String(ev.id).startsWith('plane-')) addPlaneImages(e.target)
          })
          emitView()
        }}
        onStyleData={(e: any) => addPlaneImages(e.target)}
        onMoveEnd={(_e: ViewStateChangeEvent) => emitView()}
        onMouseMove={onMouseMove}
        onMouseLeave={() => setHover(null)}
        onClick={onClick}
      >
        <Source id="apt" type="geojson" data={airportData as any}>
          <Layer
            id="apt-dot" type="circle" minzoom={5}
            paint={{
              'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2.5, 9, 5],
              'circle-color': AIR.bg,
              'circle-stroke-width': ['case', ['==', ['get', 'atc'], 1], 2, 1.2],
              // muted tones: airfields mustn't read as aircraft of the same class
              'circle-stroke-color': ['case', ['==', ['get', 'atc'], 1], AIR.accent, ['==', ['get', 'mil'], 1], '#8A6A3A', '#3E4856'],
            }}
          />
          <Layer
            id="apt-label" type="symbol" minzoom={6}
            layout={{ 'text-field': ['get', 'lbl'], 'text-font': FONT, 'text-size': 10, 'text-offset': [0, 1.1], 'text-anchor': 'top' }}
            paint={{
              'text-color': ['case', ['==', ['get', 'atc'], 1], AIR.accent, ['==', ['get', 'mil'], 1], '#A08050', AIR.muted],
              'text-halo-color': AIR.bg, 'text-halo-width': 1,
            }}
          />
        </Source>

        <Source id="trails" type="geojson" data={EMPTY}>
          <Layer
            id="trail-line" type="line"
            layout={{ 'line-cap': 'round', 'line-join': 'round' }}
            paint={{
              'line-color': colorExpr,
              'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.6, 9, 1.6],
              'line-opacity': ['case', ['==', ['get', 'c'], 'civil'], 0.28, 0.55],
            }}
          />
        </Source>

        <Source id="sel" type="geojson" data={selData as any}>
          <Layer
            id="sel-history" type="line" filter={['==', ['get', 'k'], 'history']}
            paint={{ 'line-color': AIR.privacy, 'line-width': 1.2, 'line-opacity': 0.6, 'line-dasharray': [1, 2] }}
          />
          <Layer
            id="sel-flown" type="line" filter={['==', ['get', 'k'], 'flown']}
            layout={{ 'line-cap': 'round', 'line-join': 'round' }}
            paint={{ 'line-color': AIR.watched, 'line-width': 2.2, 'line-opacity': 0.9 }}
          />
          <Layer
            id="sel-route-flown" type="line" filter={['==', ['get', 'k'], 'route-flown']}
            layout={{ 'line-cap': 'round' }}
            paint={{
              'line-color': AIR.watched,
              'line-width': ['case', ['==', ['get', 'sel'], 1], 2, 1],
              'line-opacity': ['case', ['==', ['get', 'sel'], 1], 0.85, 0.35],
            }}
          />
          <Layer
            id="sel-route-next" type="line" filter={['==', ['get', 'k'], 'route-next']}
            paint={{
              'line-color': AIR.watched,
              'line-width': ['case', ['==', ['get', 'sel'], 1], 1.6, 0.9],
              'line-opacity': ['case', ['==', ['get', 'sel'], 1], 0.75, 0.3],
              'line-dasharray': [3, 3],
            }}
          />
          <Layer
            id="sel-ring" type="line" filter={['==', ['get', 'k'], 'ring']}
            paint={{ 'line-color': AIR.accent, 'line-width': 1, 'line-opacity': 0.7, 'line-dasharray': [4, 3] }}
          />
          <Layer
            id="sel-endpoints" type="circle" filter={['==', ['get', 'k'], 'endpoint']}
            paint={{ 'circle-radius': 4.5, 'circle-color': AIR.bg, 'circle-stroke-width': 2, 'circle-stroke-color': AIR.watched }}
          />
        </Source>

        <Source id="ac" type="geojson" data={EMPTY}>
          <Layer
            id="ac-emg-halo" type="circle" filter={['==', ['get', 'c'], 'emergency']}
            paint={{ 'circle-radius': 16, 'circle-color': AIR.emergency, 'circle-opacity': 0.18, 'circle-stroke-width': 1, 'circle-stroke-color': AIR.emergency, 'circle-stroke-opacity': 0.6 }}
          />
          <Layer
            id="ac-sel-ring" type="circle" filter={['==', ['get', 'hex'], selection.hex || '']}
            paint={{ 'circle-radius': 15, 'circle-color': 'rgba(0,0,0,0)', 'circle-stroke-width': 1.5, 'circle-stroke-color': AIR.bright }}
          />
          <Layer
            id="ac-icon" type="symbol"
            layout={{
              'icon-image': ['get', 'icon'],
              'icon-rotate': ['get', 'trk'],
              'icon-rotation-alignment': 'map',
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'icon-size': ['interpolate', ['linear'], ['zoom'], 2, ['case', ['==', ['get', 'n'], 1], 0.6, 0.45], 8, ['case', ['==', ['get', 'n'], 1], 0.95, 0.75], 12, 1.1],
              'symbol-sort-key': ['case', ['==', ['get', 'c'], 'emergency'], 5, ['==', ['get', 'c'], 'watched'], 4, ['==', ['get', 'c'], 'military'], 3, ['==', ['get', 'c'], 'privacy'], 2, 1],
            }}
          />
          <Layer
            id="ac-label-notable" type="symbol" minzoom={5} filter={['==', ['get', 'n'], 1]}
            layout={{ 'text-field': ['get', 'lbl'], 'text-font': FONT, 'text-size': 10, 'text-offset': [0, 1.5], 'text-anchor': 'top', 'text-optional': true }}
            paint={{ 'text-color': colorExpr, 'text-halo-color': AIR.bg, 'text-halo-width': 1.2 }}
          />
          <Layer
            id="ac-label-civil" type="symbol" minzoom={8.5} filter={['==', ['get', 'n'], 0]}
            layout={{ 'text-field': ['get', 'lbl'], 'text-font': FONT, 'text-size': 9, 'text-offset': [0, 1.5], 'text-anchor': 'top', 'text-optional': true }}
            paint={{ 'text-color': AIR.muted, 'text-halo-color': AIR.bg, 'text-halo-width': 1 }}
          />
        </Source>

        <NavigationControl position="bottom-right" showCompass={false} />
        <ScaleControl position="bottom-right" unit="nautical" />
        <AttributionControl position="bottom-left" compact customAttribution={ATTRIBUTION} />
      </Map>

      {hover && (
        <div
          className="pointer-events-none absolute z-10 rounded-sm border px-2.5 py-1.5 text-[11px] leading-tight shadow-lg"
          style={{
            left: hover.x + 14, top: hover.y + 14, background: AIR.panel, borderColor: AIR.lineStrong,
            color: AIR.text, fontFamily: FONT_MONO,
          }}
        >
          <div className="font-semibold" style={{ color: CLASS_COLOR[classify(hover.a, live.watched)] }}>
            {hover.a.cs || hover.a.reg || hover.a.hex.toUpperCase()}
          </div>
          <div style={{ color: AIR.muted }}>
            {[hover.a.type, hover.a.reg, hover.a.op].filter(Boolean).join(' · ') || hover.a.hex.toUpperCase()}
          </div>
          <div>
            {fmtAlt(hover.a)} · {hover.a.gs ?? '—'} kt{hover.a.sq ? ` · SQK ${hover.a.sq}` : ''}
          </div>
        </div>
      )}
    </div>
  )
})

export default AirMap
